/**
 * main.tsx — mount, and the one thing that has to happen before the first paint.
 *
 * The remembered theme is applied here rather than by the usual inline snippet in `index.html`, because
 * the server's Content-Security-Policy is `script-src 'self'` with no nonce: an inline script does not run
 * in production, and only in production. The cost is honest — a cold load may show one frame of the
 * default theme before the module executes — and the alternative is a CSP exception that would also admit
 * every other inline script on the page. One frame is cheaper than that.
 *
 * `data-theme` is written only when the viewer has actually chosen. Left unset, the stylesheet follows
 * `prefers-color-scheme`, which is the right default; writing "light" unasked would pin a viewer whose
 * operating system is dark to the light palette forever. That is `seedTheme`'s whole contract, and it
 * now lives in `lib/theme.ts` beside the control that offers the third state back — the storage key was
 * previously declared here AND in `App.tsx` as two unlinked string literals, so changing one silently
 * broke the other.
 *
 * **The same CSP paragraph governs the service worker, and for a second reason.** Registering it from an
 * inline `<script>` in `index.html` is the shape every tutorial uses, and here it would work in
 * development, work in every test, and fail silently in production — the one environment where an
 * installable application matters. It is registered from this module instead, at the bottom of the file.
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { installFavicon } from "./brand/favicon";
import { initInstallCapture } from "./lib/install";
import { seedTheme } from "./lib/theme";
import "./styles.css";

seedTheme();

/**
 * Before React, deliberately, and this line is load-bearing in a way that leaves no trace when it is
 * wrong.
 *
 * Chromium fires `beforeinstallprompt` once, early, and there is no way to ask for it again — so a
 * listener attached from a component's effect can miss it entirely on a machine that paints quickly, and
 * the only symptom is that the Install button on Settings never appears for reasons nobody can reproduce.
 * `initInstallCapture` is idempotent, so the cost of calling it here is a `Set` and two listeners.
 */
initInstallCapture();

// After the theme, and outside React: the tab icon belongs to the document, not to any component, and a
// component that owned it would repaint it on every remount for no reason. It is never uninstalled — the
// document outlives the application.
installFavicon();

const container = document.getElementById("root");
if (container === null) {
  // Thrown rather than logged: without the mount point there is no application, and a silent failure here
  // presents as a blank page with a clean console — the least diagnosable outcome available.
  throw new Error("main: #root is missing from index.html");
}

/**
 * The OUTERMOST error boundary, and the reason there are two of them.
 *
 * `05-client.md` §5.1 requires a boundary and prefers it placed so "a failure in one region does not take
 * the whole application with it" — which is the one inside the shell, in `App.tsx`, around the routed
 * page body. That one cannot catch the shell: a boundary never catches a throw from a component ABOVE
 * it, and `Shell` reads the session, draws the switcher from `accessibleScopes` and renders the account
 * menu. Nor can it catch `App` itself, where the session bootstrap, the scope resolution and the routing
 * live — the three places in this client most likely to meet a shape nobody expected.
 *
 * So the inner one keeps the navigation alive when a page fails, and this one is what stands between the
 * user and a white page when the thing that failed is the navigation. Neither is redundant; each is the
 * other's blind spot.
 *
 * Inside `StrictMode` rather than around it, so the boundary gets the same development double-render
 * checks as everything below it. A component excused from them is one whose bugs are found in
 * production, and this is the component that has to work when everything else has not.
 */
createRoot(container).render(
  <StrictMode>
    <ErrorBoundary region="application">
      <App />
    </ErrorBoundary>
  </StrictMode>,
);

/**
 * The service worker — registered here, after the application is mounted, and never from `index.html`.
 *
 * ⚠️ **An inline script does not run in production and only in production** (`script-src 'self'`, no
 * nonce — see the header, and `vite.config.ts` for the same hazard costing us the module-preload
 * polyfill). A registration snippet in the document would therefore be green in development, green in
 * every test, and dead in the deployment, and the failure is a *missing* feature rather than a broken
 * one: the app would simply stop being installable, with nothing in any console to say so.
 *
 * Deferred to `load` so it competes with nothing on the first paint. This module is a deferred module
 * script and therefore runs before `load` fires, so the listener is not attached to an event that has
 * already gone — which is exactly the trap `initInstallCapture` sits on the other side of, in the
 * opposite direction. If this file is ever reached by a dynamic import instead, that stops being true.
 *
 * `web/public/sw.js` caches nothing at all (its own header explains why at length), so there is no
 * development-versus-production question to answer here and no reason to gate the registration on the
 * build: a worker that serves no request cannot serve a stale one.
 */
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {
      // Nothing is broken by this. Installability is the only thing the worker provides, so a browser
      // that refuses the registration — an insecure origin in development, a private window, a policy —
      // gets the whole application minus the ability to install it. Reporting it would be alarming out of
      // all proportion to a feature the user has not asked for yet.
    });
  });
}
